import { throttle } from 'lodash';
/**
 * @ngdoc directive
 * @name adjust.directive:scrollThumb
 * @scope
 * @restrict A
 *
 * @description
 * Scroll thumb which is child of the {@link adjust.directive:scrollBar scrollBar} directive
 * Responsible for manual container scrolling
 *
 * @param {string} instance scrolling instance
 *
 */
function scrollThumb(Helper) {
  'ngInject';
  return {
    restrict: 'A',
    scope: {},
    require: ['scrollThumb', '^scrollBar'],
    controller: 'ScrollThumbController',
    controllerAs: 'vm',
    bindToController: {
      instance: '=scrollThumb',
    },
    link: Helper.linkFunction,
  };
}

/**
 * @ngdoc controller
 * @name adjust.controller:ScrollThumbController
 *
 * @description
 * All the functionality of the {@link adjust.directive:scrollThumb scrollThumb} directive
 *
 */
function ScrollThumbController($element, $document, $timeout, $window, Helper, $scope) {
  'ngInject';
  var vm = this;

  var _lazyResize = throttle(_digestWidth, 500);
  // eslint-disable-next-line prefer-destructuring
  var _thumb = $element[0];
  var _side;
  var _table;
  var _bar;
  var _tid;
  var _diffX;

  vm.init = init;

  $element.addClass('scroll-thumb');

  $element.bind('mousedown', _handleStartMoving);
  $element.bind('mouseup', _handleStopMoving);
  $document.bind('mouseup', _handleStopMoving);

  Helper.ready(_handleReady);
  $scope.$on('rightSidebar:toggle', _digestWidth);
  angular.element($window).on('resize', _lazyResize);
  $element.on('$destroy', _handleDestroy);

  /**
   * @ngdoc method
   * @name _handleReady
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Handle ready state, when scope is digested
   *
   * @private
   */
  function _handleReady() {
    $scope.$on('scroll:resize:' + vm.instance, _digestWidth);

    vm.parentController.on('loading:' + vm.instance, _handleChange);
    vm.parentController.on('scroll:left:' + vm.instance, _handleScrollLeft);
  }

  /**
   * @ngdoc method
   * @name init
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Register parent controller
   *
   * @param {Object} parentController parent controller
   *
   */
  function init(parentController) {
    vm.parentController = parentController;
  }

  /**
   * @ngdoc method
   * @name _handleChange
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Cache bar, side and table elements once the page is loaded
   *
   * @param {boolean} loading loading state
   * @private
   */
  function _handleChange(e, loading) {
    if (loading) {
      return;
    }

    _bar = vm.parentController.getElement();
    _side = vm.parentController.getSide();
    _table = vm.parentController.getScrollTable();

    _digestWidth();
  }

  /**
   * @ngdoc method
   * @name _digestWidth
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Initiate with setting after the cycle has been digested
   *
   * @private
   */
  function _digestWidth() {
    Helper.destroyTid(_tid);

    _tid = $timeout(_setWidth, 500);
  }

  /**
   * @ngdoc method
   * @name _digestWidth
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Set width of the thumb depending on the bar width, table width and side column
   *
   * @private
   */
  function _setWidth() {
    if (!_table || !_side || !_bar) {
      return;
    }

    _thumb.style.width = Math.pow(_bar.offsetWidth, 2) / (_table.scrollWidth + _side.offsetWidth) + 'px';
  }

  /**
   * @ngdoc method
   * @name _handleScrollLeft
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Set the position of the thumb depending on the scroll left position
   *
   * @param {string} left left position of the thumb in px
   * @private
   */
  function _handleScrollLeft(left) {
    if (!_thumb || !_bar) {
      return;
    }

    _thumb.style.left = (_thumb.offsetWidth * left) / _bar.offsetWidth + 'px';
  }

  /**
   * @ngdoc method
   * @name _handleStartMoving
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Handle start moving the thumb and bind mousemove event
   *
   * @param {Object} e mousedown event
   * @private
   */
  function _handleStartMoving(e) {
    _diffX = e.clientX - _thumb.offsetLeft;

    vm.parentController.setNoSelect(true);

    $document.bind('mousemove', _handleMove);
  }

  /**
   * @ngdoc method
   * @name _handleStopMoving
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Handle stop moving the thumb and unbind mousemove event
   *
   * @private
   */
  function _handleStopMoving() {
    vm.parentController.setNoSelect(false);

    $document.unbind('mousemove', _handleMove);
  }

  /**
   * @ngdoc method
   * @name _handleMove
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Handle thumb moving by updating it's x axis and sync table horizontal position
   *
   * @param {Object} e mousemove event
   * @private
   */
  function _handleMove(e) {
    var tWidth = _thumb.offsetWidth;
    var bWidth = _bar.offsetWidth;

    var xPos = e.clientX - _diffX;

    xPos = xPos < 0 ? 0 : xPos;

    if (xPos + tWidth > bWidth) {
      return;
    }

    _thumb.style.left = xPos + 'px';
    _table.scrollLeft = (xPos * _bar.offsetWidth) / _thumb.offsetWidth;
  }

  /**
   * @ngdoc method
   * @name _handleDestroy
   * @methodOf adjust.controller:ScrollThumbController
   *
   * @description
   * Handle destroy event by unbind all events registered within this directive
   * @private
   */
  function _handleDestroy() {
    $element.unbind('mousedown', _handleStartMoving);
    $element.unbind('mouseup', _handleStopMoving);
    $document.unbind('mousemove', _handleMove);
    $document.unbind('mouseup', _handleStopMoving);
    angular.element($window).off('resize', _lazyResize);

    Helper.destroyTid(_tid);
  }
}

export { ScrollThumbController, scrollThumb };
